import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputLabel,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";

import { userSchema } from "./schema";
import { STATUS } from "../../../redux/constants";
import {
  addUser,
  editUser,
  editUserStakeholder,
  getUserDetails,
  listUsers,
  resetUserStatus,
  getManagerList,
} from "../../../redux/actions";

import CeroInput from "../../../components/CeroInput";
import CeroSelect from "../../../components/CeroSelect";
import CeroSideSheetDrawer from "../../../components/CeroSideSheetDrawer";
import { userRolesForAddUser } from "../../../constants";
import useStyles from "./styles";
import CeroChipSelect from "../../../components/CeroChipSelect";

const AddUserDrawer = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const stakeholderTypeRef = [
    { id: "internal", name: "Internal" },
    { id: "external", name: "External" },
  ];
  const addUserStatus = useSelector((state) => state.users.addUser);
  const userData = useSelector((state) => state.users.userDetails.data);
  const editUserStatus = useSelector((state) => state.users.editUser);
  const editUserStakeholderStatus = useSelector(
    (state) => state.users.editUserStakeholder
  );
  const facilitiesData = useSelector(
    (state) => state.listings.listFacilities.data
  );

  const [selectedValues, setSelectedValues] = useState(userData.role || []);

  useEffect(() => {
    if (userData.role) {
      setSelectedValues(userData.role);
    }
  }, [userData]);

  const facilitiesList = facilitiesData.reduce((acc, item) => {
    acc[item.name] = item.id;
    return acc;
  }, {});

  const stakeholderTypeList = stakeholderTypeRef.map((item) => ({
    key: item.id,
    value: item.name,
  }));

  const userForm = useFormik({
    initialValues: {
      email: userData.email || "",
      role: userData.role || [],
      manager: userData.manager_id || "",
      facility: userData.facility_id || "",
      stakeholderType: userData.stakeholder_type || "",
      facilities: userData.facilities
        ? facilitiesData
            .filter((item) => userData.facilities.includes(item.id))
            .map((item) => item.name)
        : [] || [],
    },
    validationSchema: userSchema,
    enableReinitialize: true,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (props.editUser) {
      dispatch(getUserDetails(props.editUser));
    }
    return () => dispatch(resetUserStatus());
  }, [props.editUser, dispatch]);

  const rolePriority = () => {
    if (selectedValues.includes("sustainability_manager")) {
      return "sustainability_manager";
    } else if (selectedValues.includes("approver")) {
      return "approver";
    } else if (selectedValues.includes("facility_manager")) {
      return "facility_manager";
    } else if (selectedValues.includes("business_user")) {
      return "business_user";
    } else {
      return "read_only_user";
    }
  };

  useEffect(() => {
    userForm.values.role &&
      dispatch(getManagerList(rolePriority(), userForm.values.facility));
  }, [dispatch, selectedValues, userForm.values.facility]);

  useEffect(() => {
    if (addUserStatus.status === STATUS.SUCCESS) {
      enqueueSnackbar("User added successfully", { variant: "success" });
      props.onClose(false);
      dispatch(resetUserStatus());
      dispatch(listUsers());
    } else if (addUserStatus.status === STATUS.ERROR) {
      enqueueSnackbar(addUserStatus.message.message, { variant: "error" });
    }
  }, [addUserStatus, enqueueSnackbar, dispatch, props.onClose]);

  useEffect(() => {
    if (
      editUserStatus.status === STATUS.SUCCESS ||
      editUserStakeholderStatus.status === STATUS.SUCCESS
    ) {
      enqueueSnackbar("User edited successfully", { variant: "success" });
      props.onClose(false);
      dispatch(resetUserStatus());
      dispatch(listUsers());
    } else if (
      editUserStatus.status === STATUS.ERROR ||
      editUserStakeholderStatus.status === STATUS.ERROR
    ) {
      enqueueSnackbar(
        editUserStatus.message
          ? editUserStatus.message.message
          : editUserStakeholderStatus.message.message,
        { variant: "error" }
      );
    }
  }, [
    editUserStatus,
    enqueueSnackbar,
    props.onClose,
    dispatch,
    editUserStakeholderStatus,
  ]);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const facilityHandleChange = (event) => {
    const value = event.target.value;
    setSelectedFacilities(value);
    userForm.setFieldValue("facility", value);
    if (value[value.length - 1] === "all") {
      setSelectedFacilities(
        selectedFacilities.length === Object.keys(facilitiesList).length
          ? []
          : Object.keys(facilitiesList)
      );
      return;
    }
  };
  useEffect(() => {
    userForm.setFieldValue("facilities", selectedFacilities);
  }, [selectedFacilities]);
  const onSubmitUserData = () => {
    const selectedFacilitiesToSend = [];
    facilitiesData.map(
      (item) =>
        selectedFacilities.includes(item.name) &&
        selectedFacilitiesToSend.push(item["id"])
    );
    if (props.editUser) {
      userData.is_stakeholder
        ? dispatch(
            editUserStakeholder(
              userData.id,
              userForm.values.email,
              userForm.values.role,
              userForm.values.facility,
              userForm.values.manager,
              userForm.values.stakeholderType
            )
          )
        : dispatch(
            editUser(
              props.editUser,
              userForm.values.email,
              userForm.values.role,
              selectedFacilitiesToSend,
              userForm.values.manager
            )
          );
    } else {
      dispatch(
        addUser(
          userForm.values.email,
          userForm.values.role,
          selectedFacilitiesToSend,
          userForm.values.manager,
          userForm.values.stakeholderType
        )
      );
    }
  };

  const drawerTitle = props.editUser ? "Edit User" : "Add User";

  const getPrimaryPaymentDrawer = () => {
    return (
      <Box className={classes.mainContainer}>
        <CeroInput
          required
          id="email"
          name="email"
          label="Email address"
          fullWidth
          value={userForm.values.email}
          onChange={userForm.handleChange}
          onBlur={userForm.handleBlur}
          error={userForm.errors.email}
          disabled={props.editUser}
        />
        <InputLabel id="demo-multiple-name-label">Roles</InputLabel>
        <Box className={classes.checkbox}>
          <FormGroup>
            {userRolesForAddUser.map((user) => (
              <FormControlLabel
                key={user.key}
                control={
                  <Checkbox
                    className={classes.selectCheckbox}
                    name="role"
                    checked={selectedValues.includes(user.key)}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      const updatedValues = isChecked
                        ? [...userForm.values.role, user.key]
                        : userForm.values.role.filter(
                            (item) => item !== user.key
                          );
                      userForm.setFieldValue("role", updatedValues);
                      setSelectedValues(updatedValues);
                    }}
                    value={user.key}
                    style={{
                      color: "#8C2148",
                    }}
                    disabled={
                      (selectedValues.includes("read_only_user") &&
                        user.key !== "read_only_user") ||
                      (selectedValues.length > 0 &&
                        !selectedValues.includes("read_only_user") &&
                        user.key === "read_only_user")
                    }
                  />
                }
                label={user.value}
              />
            ))}
          </FormGroup>
        </Box>
        {userForm.values.role.includes("business_user") ||
        userForm.values.role.includes("facility_manager") ? (
          <CeroChipSelect
            required
            id="facility"
            name="facility"
            label="Facility"
            optionsde={facilitiesList}
            selected={userForm.values.facilities}
            handleChange={facilityHandleChange}
            classes={{ justCheckSelected: classes.justCheckSelected }}
            formControlClass={classes.multiSelect}
          />
        ) : null}

        {userForm.values.role.includes("read_only_user") ? (
          <CeroSelect
            required
            id="stakeholderType"
            name="stakeholderType"
            label="Stake Holder Type"
            fullWidth
            options={stakeholderTypeList}
            selectedValue={userForm.values.stakeholderType}
            onChange={userForm.handleChange}
            onBlur={userForm.handleBlur}
            error={userForm.errors.stakeholderType}
          />
        ) : null}
      </Box>
    );
  };

  return (
    <CeroSideSheetDrawer
      primaryDrawer={{
        drawerOpen: props.isOpen,
        onClose: () => props.onClose(false),
        content: getPrimaryPaymentDrawer(),
        header: { title: drawerTitle },
        footer: {
          primaryBtnTitle: "Save",
          secondaryBtnTitle: "Cancel",
          primaryBtnAction: onSubmitUserData,
          secondaryBtnAction: () => props.onClose(false),
          disablePrimaryBtn: !userForm.dirty || !userForm.isValid,
        },
        classes: {
          drawerContainer: classes.drawerContainer,
          contentArea: classes.drawerContentArea,
        },
      }}
    />
  );
};

export default AddUserDrawer;
